import { useEffect } from 'react';
import { Block } from './types';

/**
 * The live block behind a pending drag move. The Confirm New Start Date
 * modal previews this block and the move payload builds from it, so a synced
 * edit in the drop-to-confirm window is what the coach sees and submits.
 *
 * A synced start-date change under the open modal invalidates the pending
 * move entirely: the proposed date was dropped relative to the old start, so
 * the shift offset no longer matches what the preview showed. The guard
 * cancels the pending move (modal closes, block reverts) and the coach drops
 * again. While saving, the guard stays out: the optimistic write already
 * moved the block, and `onSettled` clears the pending move.
 */
export function usePendingMoveLiveBlock(
  pendingMove: { block: Block; startDate: string } | null,
  blocks: Block[] | undefined,
  isSaving: boolean,
  onCancel: () => void,
): Block | null {
  const liveBlock = pendingMove
    ? (blocks?.find((b) => b.id === pendingMove.block.id) ?? pendingMove.block)
    : null;

  useEffect(() => {
    if (!pendingMove || isSaving || !liveBlock) return;
    if (liveBlock.startDate !== pendingMove.block.startDate) {
      onCancel();
    }
  }, [pendingMove, isSaving, liveBlock, onCancel]);

  return liveBlock;
}
